import { useEffect, useState } from "react";
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import { ChevronLeft, X } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  FrozenSpecimenSelector,
  type FrozenSpecimenSelection,
} from "@src/packages/care/cryovault/FrozenSpecimenSelector";

export type { FrozenSpecimenSelection } from "@src/packages/care/cryovault/FrozenSpecimenSelector";

type Props = {
  visible: boolean;
  type: "oocyte" | "embryo" | null;
  initialSpecimenIds?: string[];
  onBack: () => void;
  onConfirm: (selection: FrozenSpecimenSelection) => void;
  isLoading?: boolean;
  allowedCycleId?: string | null;
};

export function SelectFrozenSpecimensModal({
  visible,
  type,
  initialSpecimenIds,
  onBack,
  onConfirm,
  isLoading = false,
  allowedCycleId = null,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("SelectFrozenSpecimensModal");
  const { height: windowHeight } = useWindowDimensions();
  const [selection, setSelection] = useState<FrozenSpecimenSelection>({
    specimenIds: initialSpecimenIds ?? [],
    sourceCycleId: null,
  });

  useEffect(() => {
    if (visible) {
      setSelection({
        specimenIds: initialSpecimenIds ?? [],
        sourceCycleId: null,
      });
    }
  }, [visible, initialSpecimenIds]);

  if (!type) return null;

  const isEmbryo = type === "embryo";
  const title = isEmbryo
    ? t("care.select_specimens.title_embryo")
    : t("care.select_specimens.title_oocyte");
  const subtitle = isEmbryo
    ? t("care.select_specimens.subtitle_embryo")
    : t("care.select_specimens.subtitle_oocyte");
  const confirmDisabled = isLoading || selection.specimenIds.length === 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onBack}
    >
      <Pressable
        onPress={() => {
          capturePress("dismiss_backdrop");
          onBack();
        }}
        disabled={isLoading}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.5)",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            backgroundColor: EMBER.colors.surface,
            borderRadius: 20,
            width: "90%",
            maxWidth: 420,
            maxHeight: windowHeight * 0.85,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 20,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: EMBER.colors.border,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress("back");
                onBack();
              }}
              disabled={isLoading}
              hitSlop={8}
            >
              <ChevronLeft size={20} color={EMBER.colors.textLight} />
            </Pressable>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 16,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {title}
            </Text>
            <Pressable
              onPress={() => {
                capturePress("close_modal");
                onBack();
              }}
              disabled={isLoading}
              hitSlop={8}
            >
              <X size={20} color={EMBER.colors.textLight} />
            </Pressable>
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ padding: 20 }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                marginBottom: 14,
                lineHeight: 18,
              }}
            >
              {subtitle}
            </Text>

            <FrozenSpecimenSelector
              type={type}
              selectedIds={selection.specimenIds}
              onChange={setSelection}
              allowedCycleId={allowedCycleId}
            />

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("care.select_specimens.confirm")),
                  "care.select_specimens.confirm",
                );
                onConfirm(selection);
              }}
              disabled={confirmDisabled}
              style={{
                backgroundColor: confirmDisabled
                  ? EMBER.colors.textFaint
                  : EMBER.colors.warm,
                borderRadius: 12,
                paddingVertical: 14,
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                gap: 8,
                marginTop: 24,
              }}
            >
              {isLoading && <ActivityIndicator size="small" color="#FFFFFF" />}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                {isLoading
                  ? t("care.select_specimens.starting")
                  : selection.specimenIds.length === 0
                    ? t(
                        isEmbryo
                          ? "care.select_specimens.pick_at_least_one_embryo"
                          : "care.select_specimens.pick_at_least_one_egg",
                      )
                    : t("care.select_specimens.confirm")}
              </Text>
            </Pressable>

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("care.select_specimens.back")),
                  "care.select_specimens.back",
                );
                onBack();
              }}
              disabled={isLoading}
              style={{ alignItems: "center", paddingVertical: 14 }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: EMBER.colors.textLight,
                }}
              >
                {t("care.select_specimens.back")}
              </Text>
            </Pressable>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
